import { MessageContext } from "@/contexts/MessageContext";
import { memo, useContext } from "react";

import MessageLoader from "./Loader";
import { Message } from "./Message";
import { IAction, IMessageElement, IStep } from "@/client/types";
import { useConfig } from "@/client/useConfig";
import _ from "lodash";

interface Props {
  messages: IStep[];
  elements: IMessageElement[];
  actions: IAction[];
  indent: number;
  isRunning?: boolean;
  scorableRun?: IStep;
}

const GEN_RUN_NAMES = ["on_chat_start", "on_message", "on_audio_end"];

const hasToolStep = (step: IStep): boolean => {
  return (
    step.steps?.some(
      (s) => s.role === "tool" || s.role.includes("message") || hasToolStep(s)
    ) || false
  );
};

const hasAssistantMessage = (step: IStep): boolean => {
  return (
    step.steps?.some((s) => s.role === "assistant" || hasAssistantMessage(s)) ||
    false
  );
};

const Messages = memo(
  ({ messages, elements, actions, indent, isRunning, scorableRun }: Props) => {
    const messageContext = useContext(MessageContext);
    const { config } = useConfig();
    return (
      <>
        {messages.map((m) => {
          // Handle satori runs
          if (m.name && GEN_RUN_NAMES.includes(m.name)) {
            const isRunning = !m.end && !m.isError && messageContext.loading;
            const isToolCallCoT = config?.ui.cot === "tool_call";
            const isHiddenCoT = config?.ui.cot === "hidden";

            const showToolCoTLoader = isToolCallCoT
              ? isRunning && !hasToolStep(m)
              : false;

            const showHiddenCoTLoader = isHiddenCoT
              ? isRunning && !hasAssistantMessage(m)
              : false;
            // Ignore on_chat_start for scorable run
            const scorableRun =
              !isRunning && m.name !== "on_chat_start" ? m : undefined;
            return (
              <>
                {m.steps?.length ? (
                  <Messages
                    messages={m.steps}
                    elements={elements}
                    actions={actions}
                    indent={indent}
                    isRunning={isRunning}
                    scorableRun={scorableRun}
                  />
                ) : null}
                <MessageLoader
                  show={showToolCoTLoader || showHiddenCoTLoader}
                />
              </>
            );
          } else {
            // Score the current run
            const _scorableRun = m.role === "run" ? m : scorableRun;
            // The message is scorable if it is the last assistant message of the run
            const isScorable =
              m ===
              _.findLast(
                _scorableRun?.steps,
                (_m: { role: string }) => _m.role === "assistant"
              );
            return (
              <Message
                message={m}
                elements={elements}
                actions={actions}
                key={m.id}
                indent={indent}
                isRunning={isRunning}
                scorableRun={_scorableRun}
                isScorable={isScorable}
              />
            );
          }
        })}
      </>
    );
  }
);

export { Messages };
